'use client'

import { useActionState } from 'react'
import {
  DialogClose,
  DialogFooter,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Alert,
  AlertTitle,
  Spinner,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@anytimefitness/pb-react-ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircleIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import ActionsError from '@/components/actions-error'
import { addNewLead } from '@/server/actions/leads'
import {
  ChannelOptions,
  NewLeadSchema,
  type AddNewLeadResponse,
  type NewLeadData,
} from '@/types/leads'

const initState = {
  success: false,
  message: '',
  error: undefined,
  validationError: undefined,
} satisfies AddNewLeadResponse

export default function CreateLeadForm() {
  const form = useForm<NewLeadData>({
    resolver: zodResolver(NewLeadSchema),
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      channel: ChannelOptions.WalkIn,
    },
  })

  const [state, formAction, isPending] = useActionState<
    AddNewLeadResponse,
    FormData
  >(async (_, formData) => {
    const data = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      channel: formData.get('channel'),
    }

    try {
      const response = await addNewLead(data as NewLeadData)
      if (response.success) {
        form.reset()
      }

      return response
    } catch (err) {
      return {
        success: false,
        message: (err as Error)?.message ?? 'An error occurred',
        error: (err as Error)?.message ?? 'Unknown error',
      }
    }
  }, initState)

  return (
    <Form {...form}>
      <form action={formAction}>
        <fieldset className="space-y-3 pt-1 pb-3" disabled={isPending}>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="First Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Last Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input placeholder="Phone" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="channel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Channel</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  {...field}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Channel" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={ChannelOptions.WalkIn}>
                      Walk In
                    </SelectItem>
                    <SelectItem value={ChannelOptions.Telephone}>
                      Telephone
                    </SelectItem>
                    <SelectItem value={ChannelOptions.Grassroots}>
                      Grassroots
                    </SelectItem>
                    <SelectItem value={ChannelOptions.PaidSocial}>
                      Paid Social
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
        <ActionsError state={state} className="mb-3" />
        {state.success && (
          <Alert className="mb-3">
            <CheckCircleIcon className="stroke-green-700" />
            <AlertTitle className="text-green-700">
              Lead has been created!
            </AlertTitle>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button type="submit" disabled={isPending || !form.formState.isValid}>
            {isPending && <Spinner />}
            Submit Lead Information
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
